import {
    addToChainAfter,
    ApiHandlerDiscreteParameter,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { AddCartLinesApiParameter, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";
import {
    chain,
    PopulateAddLineCollectionApiParameter,
} from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import { tryParseSelections } from "@tSurya/Common/MgbwConfiguratorHelpers";
import { MGBW_SELECTIONS, MGBW_SELECTIONS_INPUT } from "@tSurya/Common/MgbwPropertyKeys";

type HandlerType = ApiHandlerDiscreteParameter<
    { order?: CartModel } & HasOnSuccess & HasOnError<string>,
    AddCartLinesApiParameter,
    CartModel,
    { updateCartApiParameter: UpdateCartApiParameter }
>;

/**
 * Placing a saved order re-adds its lines to the current cart. The stored configurator
 * selections live under the `mgbw_selections` property, but the backend AddCartLine
 * pipeline only reads the transient `mgbw_selectionsInput` key. Without this step the
 * selections are dropped and the line is added unconfigured. Convert the persisted
 * selections into the input format so the configurator data is rebuilt on the new line.
 */
export const AddConfiguratorSelectionsInput: HandlerType = props => {
    const cartLines = props.apiParameter?.cartLineCollection?.cartLines;
    if (!cartLines?.length) {
        return;
    }

    props.apiParameter = {
        ...props.apiParameter,
        cartLineCollection: {
            ...props.apiParameter.cartLineCollection,
            cartLines: cartLines.map(cartLine => {
                const maybeSelections = tryParseSelections(cartLine.properties?.[MGBW_SELECTIONS]);
                if (!maybeSelections) {
                    return cartLine;
                }

                return {
                    ...cartLine,
                    properties: {
                        ...(cartLine.properties || {}),
                        [MGBW_SELECTIONS_INPUT]: maybeSelections,
                    },
                };
            }),
        },
    };
};

export const RemoveMadeToOrderFromRequest: HandlerType = props => {
    const cartLines = props.apiParameter?.cartLineCollection?.cartLines;

    if (!cartLines?.length) {
        return;
    }

    props.apiParameter = {
        ...props.apiParameter,
        cartLineCollection: {
            ...props.apiParameter.cartLineCollection,
            cartLines: cartLines.map(cartLine => {
                const { isMadeToOrder, ...properties } =
                    cartLine.properties ?? {};

                return {
                    ...cartLine,
                    properties,
                };
            }),
        },
    };
};

addToChainAfter(chain, PopulateAddLineCollectionApiParameter, AddConfiguratorSelectionsInput);
addToChainAfter(chain, AddConfiguratorSelectionsInput, RemoveMadeToOrderFromRequest);
